/**
 * ConversationModelPicker: the composer's model control.
 * Replaces the room-only `ConversationModelSelect`.
 *
 * Every viewer gets a `DropdownMenu` with Low, Medium and High, each with its price per message
 * (`conversationMessageCost(baseRate, multiplier)`). An entitled viewer (`useConversationModelEnabled`,
 * the only flag) also gets a locked "Advanced models" submenu: every `CONVERSATION_MODELS` option,
 * grouped by `CONVERSATION_MODEL_TIER` and named by `CONVERSATION_MODEL_LABELS`, so a new option
 * (a new blend) appears with no change here. For everyone else that entry is not in the DOM at
 * all. The trigger shows the level, or the advanced option's name while one is picked (D1: a
 * present `conversationModel` wins for an entitled owner, and is ignored for anyone else).
 *
 * `isReadOnly` (a collaborator in someone else's room, D13) shows the room's
 * `effectiveConversation`, resolved server-side from the OWNER's gates: the level (or the
 * advanced option) and its price, with no menu and independent of the viewer's own flags.
 * Nothing renders without it. Prices hide when `baseRate` is unknown.
 *
 * Internal options (`CONVERSATION_MODEL_INTERNAL_OPTIONS`, plan D3) are listed only for verified
 * Fairgen staff (`useConversationModelInternalEnabled`), with a plain label from the id and no
 * customer i18n key. For anyone else they are not in the DOM, and a stored one is ignored.
 */

import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, Gauge, Lock } from '@digital-twins/design-system/icons'

import {
  composerTriggerClassName,
  composerTriggerCaptionClassName,
  composerTriggerChevronClassName,
  type ComposerAppearance,
} from '@/components/composer/composerAppearance'

import { CreditIcon } from '@/components/icons/CreditIcon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useConversationModelEnabled } from '@/hooks/useConversationModelEnabled'
import { useConversationModelInternalEnabled } from '@/hooks/useConversationModelInternalEnabled'
import { formatCreditAmount } from '@/lib/billing/formatCreditAmount'
import { cn } from '@/lib/utils'

import {
  CONVERSATION_LEVELS,
  CONVERSATION_LEVEL_CREDIT_MULTIPLIER,
  CONVERSATION_MODELS,
  CONVERSATION_MODEL_LABELS,
  CONVERSATION_MODEL_TIER,
  CONVERSATION_MODEL_TIERS,
  CONVERSATION_TIER_CREDIT_MULTIPLIER,
  ChatRoomEffectiveConversation,
  ConversationLevel,
  ConversationModel,
  ConversationModelTier,
  InternalConversationModel,
  conversationMessageCost,
  isInternalConversationModel,
} from '@digital-twins/shared-types'

const CONVERSATION_MODEL_TIER_LABEL_KEYS = {
  low: 'roomPane.modelTierLow',
  medium: 'roomPane.modelTierMedium',
  high: 'roomPane.modelTierHigh',
  blend: 'roomPane.modelTierBlend',
} as const satisfies Record<ConversationModelTier, string>

const CONVERSATION_LEVEL_LABEL_KEYS = {
  low: 'roomPane.levelLow',
  medium: 'roomPane.levelMedium',
  high: 'roomPane.levelHigh',
} as const satisfies Record<ConversationLevel, string>

interface AdvancedModelGroup {
  tier: ConversationModelTier
  options: Array<ConversationModel>
}

/**
 * Tier groups in display order, options in `CONVERSATION_MODELS` order; a tier with no option is
 * skipped. Internal options are listed only when `isInternalVisible` (staff).
 */
function advancedModelGroups(isInternalVisible: boolean): Array<AdvancedModelGroup> {
  const visible = CONVERSATION_MODELS.filter((option) => isInternalVisible || !isInternalConversationModel(option))
  return CONVERSATION_MODEL_TIERS.map((tier) => ({
    tier,
    options: visible.filter((option) => CONVERSATION_MODEL_TIER[option] === tier),
  })).filter((group) => group.options.length > 0)
}

/** A staff-only option's plain label, derived from its id (never a customer i18n key). */
function internalModelLabel(option: InternalConversationModel): string {
  return `${option} (internal)`
}

/** The credits one message costs at `multiplier`, or null when the base rate is unknown. */
function priceFor(baseRate: number | null | undefined, multiplier: number): number | null {
  if (typeof baseRate !== 'number') {
    return null
  }
  return conversationMessageCost(baseRate, multiplier)
}

interface PriceTagProps {
  value: number | null
  testId: string
}

function PriceTag(props: PriceTagProps) {
  const { value, testId } = props

  if (value === null) {
    return null
  }

  return (
    <span
      className="ml-auto inline-flex shrink-0 items-center gap-1 pl-3 text-xs font-medium tabular-nums text-muted-foreground"
      data-testid={testId}
    >
      <CreditIcon className="h-3 w-3" />
      {formatCreditAmount(value)}
    </span>
  )
}

export interface ConversationModelPickerProps {
  /**
   * The stored level; a stored room without one arrives as `DEFAULT_CONVERSATION_LEVEL` (Low),
   * a new room's composer passes `DEFAULT_NEW_ROOM_LEVEL` (Medium).
   */
  level: ConversationLevel
  /** The stored advanced pick (`settings.conversationModel`), when present. */
  advancedModel?: ConversationModel | null
  /** Base credits per message before any multiplier. Unknown hides every price. */
  baseRate?: number | null
  /**
   * Extra multiplier on every listed price: the new-video rule (credit metering D15) when the
   * message being written adds a video the room has not watched. Default 1.
   */
  priceMultiplier?: number
  /** Label and price only, no menu (a collaborator in the owner's room). */
  isReadOnly?: boolean
  /**
   * Read-only source: the room DTO's `effectiveConversation` (owner's gates, final price).
   * Read-only renders nothing without it.
   */
  effectiveConversation?: ChatRoomEffectiveConversation | null
  onLevelChange?: (level: ConversationLevel) => void
  onAdvancedChange?: (model: ConversationModel) => void
  disabled?: boolean
  /** Prefix of every data-testid (`-trigger`, `-level-<id>`, `-advanced`, `-option-<id>`). */
  testIdPrefix?: string
  appearance?: ComposerAppearance
}

export function ConversationModelPicker(props: ConversationModelPickerProps) {
  const {
    level,
    advancedModel = null,
    baseRate = null,
    priceMultiplier = 1,
    isReadOnly = false,
    effectiveConversation = null,
    onLevelChange,
    onAdvancedChange,
    disabled = false,
    testIdPrefix = 'chat-composer-model',
    appearance = 'external',
  } = props

  const { t } = useTranslation('app')
  const extraMultiplier = priceMultiplier > 0 ? priceMultiplier : 1

  const isAdvancedEnabled = useConversationModelEnabled()
  const isInternalEnabled = useConversationModelInternalEnabled()

  const groups = advancedModelGroups(isInternalEnabled)
  // A stored internal pick is ignored for a non-staff viewer, like the server does.
  const visibleAdvancedModel =
    advancedModel && isInternalConversationModel(advancedModel) && !isInternalEnabled ? null : advancedModel

  function modelLabel(option: ConversationModel): string {
    if (isInternalConversationModel(option)) {
      return internalModelLabel(option)
    }
    return CONVERSATION_MODEL_LABELS[option]
  }

  function onLevelSelect(next: ConversationLevel) {
    if (onLevelChange) {
      onLevelChange(next)
    }
  }

  function onAdvancedSelect(next: ConversationModel) {
    if (onAdvancedChange) {
      onAdvancedChange(next)
    }
  }

  /* READ-ONLY follows the room owner's gates, never the viewer's: the server resolved them
     into `effectiveConversation` with the charge's own logic. */
  if (isReadOnly) {
    if (!effectiveConversation) {
      return null
    }
    const readOnlyAdvanced = effectiveConversation.advancedModel
    // The owner's internal pick reads as its tier for a non-staff viewer (same price).
    const readOnlyLabel = !readOnlyAdvanced
      ? t(CONVERSATION_LEVEL_LABEL_KEYS[effectiveConversation.level])
      : isInternalConversationModel(readOnlyAdvanced) && !isInternalEnabled
        ? t(CONVERSATION_MODEL_TIER_LABEL_KEYS[CONVERSATION_MODEL_TIER[readOnlyAdvanced]])
        : modelLabel(readOnlyAdvanced)
    return (
      <span
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs text-muted-foreground"
        data-testid={`${testIdPrefix}-readonly`}
      >
        <span className="sr-only">{t('roomPane.modelLabel')}</span>
        <span data-testid={`${testIdPrefix}-readonly-label`}>{readOnlyLabel}</span>
        <PriceTag value={effectiveConversation.creditsPerMessage} testId={`${testIdPrefix}-readonly-price`} />
      </span>
    )
  }

  /* D1: a stored advanced pick wins for an entitled owner, and is ignored for anyone else. */
  const activeAdvanced = visibleAdvancedModel && isAdvancedEnabled ? visibleAdvancedModel : null
  const activeLabel = activeAdvanced ? modelLabel(activeAdvanced) : t(CONVERSATION_LEVEL_LABEL_KEYS[level])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('roomPane.modelLabel')}
        disabled={disabled}
        data-testid={`${testIdPrefix}-trigger`}
        className={cn(
          composerTriggerClassName(appearance),
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        )}
      >
        <Gauge className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span
          className={composerTriggerCaptionClassName(appearance)}
          data-testid={`${testIdPrefix}-trigger-label`}
        >
          {activeLabel}
        </span>
        <ChevronDown
          className={cn('h-3 w-3 shrink-0 opacity-70', composerTriggerChevronClassName(appearance))}
          aria-hidden="true"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" collisionPadding={8} className="min-w-[12rem]">
        {CONVERSATION_LEVELS.map((option) => (
          <DropdownMenuItem
            key={option}
            onSelect={() => onLevelSelect(option)}
            data-testid={`${testIdPrefix}-level-${option}`}
            className="gap-2"
          >
            <Check
              className={cn('h-3.5 w-3.5', !activeAdvanced && option === level ? 'opacity-100' : 'opacity-0')}
              aria-hidden="true"
            />
            <span>{t(CONVERSATION_LEVEL_LABEL_KEYS[option])}</span>
            <PriceTag
              value={priceFor(baseRate, CONVERSATION_LEVEL_CREDIT_MULTIPLIER[option] * extraMultiplier)}
              testId={`${testIdPrefix}-level-${option}-price`}
            />
          </DropdownMenuItem>
        ))}
        {isAdvancedEnabled ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid={`${testIdPrefix}-advanced`} className="gap-2">
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                <span>{t('roomPane.advancedModels')}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent
                data-testid={`${testIdPrefix}-advanced-content`}
                avoidCollisions
                collisionPadding={8}
                className="w-max min-w-0"
              >
                {groups.map(({ tier, options }) => (
                  <DropdownMenuGroup key={tier} data-testid={`${testIdPrefix}-group-${tier}`}>
                    <DropdownMenuLabel className="flex items-center text-xs font-medium text-muted-foreground">
                      <span>{t(CONVERSATION_MODEL_TIER_LABEL_KEYS[tier])}</span>
                      <PriceTag
                        value={priceFor(baseRate, CONVERSATION_TIER_CREDIT_MULTIPLIER[tier] * extraMultiplier)}
                        testId={`${testIdPrefix}-group-${tier}-price`}
                      />
                    </DropdownMenuLabel>
                    {options.map((option) => (
                      <DropdownMenuItem
                        key={option}
                        onSelect={() => onAdvancedSelect(option)}
                        data-testid={`${testIdPrefix}-option-${option}`}
                        className="gap-2"
                      >
                        <Check
                          className={cn('h-3.5 w-3.5', activeAdvanced === option ? 'opacity-100' : 'opacity-0')}
                          aria-hidden="true"
                        />
                        <span>{modelLabel(option)}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuGroup>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
